// Corpus: the library read as data — every tune the app can open, one entry
// per tune — from the same precomputed statistics the home page draws
// (public/library-stats.json via src/data/libraryStats.ts), so the two pages
// always agree. The analysis runs at build time (src/music/libraryStats.ts
// over src/music/corpus.ts), the voice-leading league included; this page only
// fetches, formats and draws.

import { Link } from 'react-router-dom';
import { fifthsIndex } from '@/components/keyWheel';
import { tuneHref } from '@/components/tuneHref';
import { useLibraryStatsState } from '@/data/libraryStats';
import type { RankedCount } from '@/music/corpus';
import type {
  LibraryStats,
  LibraryStatsGroup,
  LibraryStatsKey,
  LibraryStatsLeagueRow,
} from '@/music/libraryStats';

// ---------------------------------------------------------------------------
// Presentational pieces (single-hue bars, direct labels, app tokens)
// ---------------------------------------------------------------------------

const fmt = (v: number, dp = 0) =>
  v.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });

function StatTile({ value, label }: { value: string; label: string }) {
  return (
    <div className="corpus-tile">
      <div className="corpus-tile-value">{value}</div>
      <div className="corpus-tile-label">{label}</div>
    </div>
  );
}

function BarList({
  title,
  rows,
  note,
  format = (v) => fmt(v),
}: {
  title: string;
  rows: RankedCount[];
  note?: string;
  format?: (v: number) => string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <section className="corpus-card">
      <h3>{title}</h3>
      {note && <p className="corpus-note">{note}</p>}
      <div className="bar-list">
        {rows.map((r) => (
          <div key={r.label} className="bar-row" title={`${r.label}: ${format(r.count)}`}>
            <span className="bar-label">{r.label}</span>
            <span className="bar-track">
              <span className="bar-fill" style={{ width: `${(100 * r.count) / max}%` }} />
            </span>
            <span className="bar-value">{format(r.count)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function League({
  stats,
  title,
  rows,
  note,
  unit,
  dp = 2,
  scale = 1,
}: {
  stats: LibraryStats;
  title: string;
  rows: LibraryStatsLeagueRow[];
  note?: string;
  unit: string;
  dp?: number;
  /** Multiplies each value for display (a share shown as a percentage). */
  scale?: number;
}) {
  return (
    <section className="corpus-card">
      <h3>{title}</h3>
      {note && <p className="corpus-note">{note}</p>}
      <ol className="corpus-league">
        {rows.map((row) => {
          const tune = stats.tunes[row.tune];
          return (
            <li key={row.tune}>
              <span className="league-title">
                <Link to={tuneHref(tune.t, tune.c, tune.rb ? 'realbook' : undefined)}>
                  {tune.t}
                </Link>
              </span>
              <span className="league-composer">{tune.c}</span>
              <span className="league-value">
                {fmt(scale * row.value, dp)} {unit}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** A bar per group: how many tunes it holds. */
const groupCounts = (groups: readonly LibraryStatsGroup[]): RankedCount[] =>
  groups.map((group) => ({ label: group.label, count: group.tunes.length }));

/** Keys of one mode round the circle of fifths, flatwards from C. */
function keyCounts(keys: readonly LibraryStatsKey[], minor: boolean): RankedCount[] {
  return keys
    .filter((key) => key.minor === minor)
    .map((key) => ({ key, at: (12 - fifthsIndex(key.pc)) % 12 }))
    .sort((a, b) => a.at - b.at || b.key.tunes.length - a.key.tunes.length)
    .map(({ key }) => ({ label: key.label, count: key.tunes.length }));
}

// ---------------------------------------------------------------------------

export function CorpusPage() {
  const { stats, failed } = useLibraryStatsState();

  if (failed) {
    return (
      <div className="corpus-page">
        <div className="page-header">
          <h1>Corpus</h1>
          <p role="alert">Statistics unavailable.</p>
        </div>
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="corpus-page">
        <div className="page-header">
          <h1>Corpus</h1>
          <p data-testid="corpus-loading">Reading the library…</p>
        </div>
      </div>
    );
  }

  const { totals, vocabulary, leagues } = stats;
  return (
    <div className="corpus-page">
      <div className="page-header">
        <h1>Corpus</h1>
        <p>Statistics for the whole library: keys, forms, chords and progressions.</p>
      </div>

      <div className="corpus-tiles" data-testid="corpus-stats">
        <StatTile value={fmt(totals.tunes)} label="tunes" />
        <StatTile value={fmt(totals.composers)} label="composers" />
        <StatTile value={fmt(totals.chords)} label="chord events" />
        <StatTile value={fmt(totals.meanBars, 1)} label="bars per form (avg)" />
        <StatTile value={fmt(totals.meanChordsPerBar, 2)} label="chords per bar (avg)" />
        <StatTile
          value={`${fmt(100 * totals.iiVShare, 1)}%`}
          label="of all changes are a ii→V step"
        />
        <StatTile
          value={`${fmt(100 * totals.iiVITuneShare, 1)}%`}
          label="of tunes resolve a full ii→V→I"
        />
        <StatTile value={`${fmt(100 * totals.minorShare, 1)}%`} label="in minor keys" />
      </div>

      <div className="corpus-grid">
        <BarList
          title="Major keys"
          note="In circle-of-fifths order."
          rows={keyCounts(stats.keys, false)}
        />
        <BarList title="Minor keys" rows={keyCounts(stats.keys, true)} />
        <BarList
          title="Chord quality families"
          note="Every chord event, collapsed to ∆ / m / 7 / ø / °."
          rows={vocabulary.families}
        />
        <BarList title="Chord types (top 15)" rows={vocabulary.qualities} />
        <BarList
          title="Root motion"
          note="Interval between consecutive chord roots."
          rows={stats.rootMotion}
        />
        <BarList
          title="Degrees (top 15)"
          note="Chords as Roman numerals relative to the key."
          rows={vocabulary.degrees}
        />
        <BarList
          title="Most common pairs (bigrams)"
          note="Two-chord sequences, relative to the key."
          rows={vocabulary.bigrams}
        />
        <BarList title="Most common three-chord sequences (trigrams)" rows={vocabulary.trigrams} />
        <BarList
          title="Form lengths"
          note="Bars as played, repeats included."
          rows={groupCounts(stats.forms)}
          format={(v) => `${fmt(v)} tunes`}
        />
        <BarList title="Feels" rows={groupCounts(stats.feels)} />
        <BarList title="Time signatures" rows={vocabulary.timeSigs} />
        <BarList
          title="Harmonic rhythm"
          note="How long a chord lasts, in beats."
          rows={vocabulary.durations}
        />
      </div>

      <h2>Rankings</h2>
      <div className="corpus-grid">
        <League
          stats={stats}
          title="Least predictable changes"
          note="Mean surprisal of a tune's chord changes under a model built from the library. Higher means less common changes."
          rows={leagues.adventurous}
          unit="bits"
        />
        <League
          stats={stats}
          title="Most predictable changes"
          rows={leagues.predictable}
          unit="bits"
        />
        <League
          stats={stats}
          title="Densest harmony"
          note="Chord changes per played bar."
          rows={leagues.densest}
          unit="chords/bar"
        />
        <League stats={stats} title="Longest forms" rows={leagues.longest} unit="bars" dp={0} />
        <League
          stats={stats}
          title="Highest share of ii→V"
          note="Share of a tune's transitions that are a ii→V step."
          rows={leagues.iiV}
          unit="%"
          dp={0}
          scale={100}
        />
        <League
          stats={stats}
          title="Hardest to voice-lead"
          note="Mean fret movement per chord with whole-line drop 2 voice leading on the middle strings."
          rows={leagues.hardest}
          unit="frets/chord"
        />
        <League
          stats={stats}
          title="Easiest to voice-lead"
          rows={leagues.easiest}
          unit="frets/chord"
        />
      </div>
    </div>
  );
}
